import { useCallback, useEffect, useRef, useState } from "react";

/**
 * LogoFocalCropper
 *
 * Lightweight, dependency-free focal-point picker for the Pro Logo Pin.
 * The owner drags a circular handle over a preview of their uploaded
 * logo; the handle's position (as percent x/y of the image) becomes the
 * CSS `object-position` used when the same logo is rendered inside the
 * circular map pin.
 *
 * Why not react-easy-crop:
 *   We don't actually re-crop the image. The logo file stays as-is,
 *   we just store two integers (logo_pin_x, logo_pin_y) on the draft
 *   and apply them as `object-position` at render time. That keeps:
 *     * No new npm dependency (~50KB savings + audit surface)
 *     * No double-upload flow (the logo is uploaded once)
 *     * Trivial to disable / change later — server-side change is two
 *       fields, frontend is one component, that's it.
 *
 * UX:
 *   * The image preview is rendered FULL — owner sees their entire logo.
 *   * A circular dashed overlay (matching the map pin's circle) sits on
 *     top, anchored at the focal point, showing exactly what the pin
 *     will look like in production.
 *   * The whole tile is draggable (mouse + touch) — wherever you press,
 *     that becomes the new focal point.
 *   * Snap to center on double-click, and a "Reset to center" button.
 *
 * Props:
 *   src       — image URL (relative or absolute is fine)
 *   x, y      — current focal point in percent 0..100
 *   onChange  — called with {x, y} as soon as the pointer moves (live)
 */
export default function LogoFocalCropper({ src, x = 50, y = 50, onChange }) {
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  // Pointer position → percent inside the tile. Clamped 0..100 so the
  // focal point can never end up outside the image (which would mean
  // the map pin showed a transparent corner).
  const update = useCallback(
    (clientX, clientY) => {
      const el = boxRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const px = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
      const py = Math.max(0, Math.min(100, ((clientY - r.top) / r.height) * 100));
      onChange?.({ x: Math.round(px), y: Math.round(py) });
    },
    [onChange],
  );

  // Global pointermove/up — bound only while dragging so a mis-click
  // elsewhere doesn't accidentally move the focal point.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const t = e.touches?.[0];
      const cx = t ? t.clientX : e.clientX;
      const cy = t ? t.clientY : e.clientY;
      update(cx, cy);
      e.preventDefault?.();
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, update]);

  const startDrag = (e) => {
    setDragging(true);
    const t = e.touches?.[0];
    update(t ? t.clientX : e.clientX, t ? t.clientY : e.clientY);
  };

  const reset = () => onChange?.({ x: 50, y: 50 });

  return (
    <div
      className="rounded-xl border border-border bg-background/50 p-3 space-y-2"
      data-testid="logo-focal-cropper"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium leading-tight">Logo focal point</div>
          <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
            Drag the circle to choose which part of your logo stays visible
            inside the round map pin. Tap "Reset" to recentre.
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="flex-none text-xs font-semibold text-primary hover:text-primary/80 underline-offset-2 hover:underline"
          data-testid="logo-focal-reset"
        >
          Reset
        </button>
      </div>

      <div className="flex gap-3 items-stretch">
        {/* Draggable preview tile — the full logo with a circular
            overlay showing what the map pin will display. */}
        <div
          ref={boxRef}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          onDoubleClick={reset}
          className="relative aspect-square w-40 min-w-[140px] rounded-xl overflow-hidden bg-checker cursor-grab active:cursor-grabbing select-none touch-none"
          style={{
            backgroundImage:
              "linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%), linear-gradient(45deg, #e5e7eb 25%, transparent 25%, transparent 75%, #e5e7eb 75%)",
            backgroundSize: "16px 16px",
            backgroundPosition: "0 0, 8px 8px",
          }}
          data-testid="logo-focal-stage"
        >
          {/* The full logo, untouched */}
          <img
            src={src}
            alt=""
            draggable={false}
            className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          />
          {/* Circular "this is what the pin will show" overlay. We use
              a CSS clip-path so the *outside* of the focal circle is
              darkened — same trick photo cropping tools use. */}
          <div
            className="absolute inset-0 bg-black/45 pointer-events-none transition-[clip-path] duration-100"
            style={{
              clipPath: `path(evenodd, "M0 0 H 100% V 100% H 0 Z M ${x}% ${y}% m -28 0 a 28 28 0 1 0 56 0 a 28 28 0 1 0 -56 0")`,
              WebkitClipPath: `path(evenodd, "M0 0 H 100% V 100% H 0 Z M ${x}% ${y}% m -28 0 a 28 28 0 1 0 56 0 a 28 28 0 1 0 -56 0")`,
            }}
          />
          {/* The handle ring — pure cosmetic, signals "drag me". */}
          <div
            className="absolute h-14 w-14 rounded-full border-2 border-white shadow-[0_0_0_2px_#ea580c] pointer-events-none transition-[left,top] duration-75"
            style={{
              left: `calc(${x}% - 28px)`,
              top: `calc(${y}% - 28px)`,
            }}
            data-testid="logo-focal-handle"
          />
        </div>

        {/* Live map-pin preview at the actual size + ring shipped on
            the map. This is the "what does it look like in production"
            answer — owners can compare it side-by-side with the source
            logo on the left. */}
        <div className="flex-1 flex flex-col items-center justify-center gap-2">
          <div
            className="h-16 w-16 rounded-full bg-white grid place-items-center overflow-hidden p-[1px]"
            style={{
              boxShadow:
                "0 0 0 2px #ea580c, 0 4px 12px rgba(15, 23, 42, 0.22)",
            }}
            data-testid="logo-focal-pin-preview"
          >
            <img
              src={src}
              alt=""
              className="h-[60px] w-[60px] rounded-full object-cover"
              style={{ objectPosition: `${x}% ${y}%` }}
            />
          </div>
          <div className="text-[11px] font-semibold text-muted-foreground text-center leading-tight">
            Map pin preview<br />
            <span className="text-foreground tabular-nums">
              {x}% / {y}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
